import { Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Popover, TextField } from '@mui/material'
import { Search as SearchIcon, CalendarMonth } from '@mui/icons-material'
import { FC, useCallback, useEffect, useState } from 'react'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api, numberWithCommas } from '../../helpers'
import { getCurrencyByPhone } from '../../helpers/countries'
import { Connect, mapStateToProps } from '../../Redux'
import { Search, SearchIconWrapper, StyledInputBase } from '../Home'
import _ from 'lodash'

interface Game {
    tableId: string
    tableName: string
    placeName: string
    owner: string
    phone: string
    totalRevenue: number
    totalGames: number
}

interface StateProps {
    games: Game[]
    loading: boolean
    searchVal: string | null
    startDate: string
    endDate: string
    calendarAnchor: HTMLElement | null
}

const headers = ['Table', 'Place', 'Owner', 'Total Games', 'Total Revenue']

const today = () => new Date().toISOString().split('T')[0]

const formatLabel = (start: string, end: string) => {
    const fmt = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    return start === end ? fmt(start) : `${fmt(start)} – ${fmt(end)}`
}

const Games: FC = () => {
    const [state, setState] = useState<StateProps>({
        games: [],
        loading: true,
        searchVal: null,
        startDate: today(),
        endDate: today(),
        calendarAnchor: null
    })
    const { games, loading, searchVal, startDate, endDate, calendarAnchor } = state

    const getGames = ({ searchVal, start, end }: { searchVal?: string | null; start?: string; end?: string } = {}) => {
        setState(s => ({ ...s, loading: true }))
        Api({
            controller: 'Payments',
            action: 'getGames',
            searchVal,
            startDate: start ?? startDate,
            endDate: end ?? endDate
        }).then((resp: any) => {
            setState(s => ({ ...s, games: resp, loading: false, searchVal: searchVal ?? null }))
        }).catch(() => {
            setState(s => ({ ...s, loading: false }))
        })
    }

    const searchGames = (val: string) => getGames({ searchVal: val })
    const startSearch = useCallback(_.debounce(searchGames, 1000), [])

    useEffect(() => {
        getGames({ start: today(), end: today() })
    }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Games</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, px: '5px' }}>
                <Search sx={{ borderRadius: 3, flex: 1, margin: 0 }}>
                    <SearchIconWrapper>
                        <SearchIcon sx={{ fontSize: 20, color: 'black' }} />
                    </SearchIconWrapper>
                    <StyledInputBase
                        placeholder='Search'
                        inputProps={{ 'aria-label': 'search' }}
                        onChange={(e) => startSearch(e.target.value)}
                    />
                </Search>
                <Button
                    variant='outlined'
                    startIcon={<CalendarMonth />}
                    sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                    onClick={e => setState(s => ({ ...s, calendarAnchor: e.currentTarget }))}
                >
                    {formatLabel(startDate, endDate)}
                </Button>
            </Box>

            <Popover
                open={Boolean(calendarAnchor)}
                anchorEl={calendarAnchor}
                onClose={() => setState(s => ({ ...s, calendarAnchor: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 260 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>Date Range</Typography>
                    <TextField
                        size='small'
                        type='date'
                        label='From'
                        value={startDate}
                        onChange={e => setState(s => ({ ...s, startDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <TextField
                        size='small'
                        type='date'
                        label='To'
                        value={endDate}
                        onChange={e => setState(s => ({ ...s, endDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size='small' onClick={() => setState(s => ({ ...s, calendarAnchor: null }))}>Cancel</Button>
                        <Button
                            size='small'
                            variant='contained'
                            onClick={() => {
                                setState(s => ({ ...s, calendarAnchor: null }))
                                getGames({ start: startDate, end: endDate })
                            }}
                        >
                            Apply
                        </Button>
                    </Box>
                </Box>
            </Popover>

            <Show>
                <Show.When isTrue={loading || !!(searchVal && !games.length)}>
                    <EmptyView
                        loading={loading}
                        oopTxt='No Games'
                        text='No games found.'
                    />
                </Show.When>
                <Show.Else>
                    <TableContainer component={Paper} sx={{ mt: 2, mx: '5px' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {headers.map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {games.map(({ tableId, tableName, placeName, owner, phone, totalRevenue, totalGames }) => (
                                    <TableRow key={tableId} hover>
                                        <TableCell>{tableName}</TableCell>
                                        <TableCell>{placeName}</TableCell>
                                        <TableCell>{owner}</TableCell>
                                        <TableCell>{totalGames}</TableCell>
                                        <TableCell>{getCurrencyByPhone(phone)} {numberWithCommas(totalRevenue)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Show.Else>
            </Show>
        </Box>
    )
}

export default Connect(mapStateToProps)(Games)
